import { type VFC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import GenericDetailPageLayout from "../core/navigation-panel/GenericDetailPageLayout.js";
import { type IRouteNavItemProps } from "../core/navigation-panel/RouteNavItem.js";
import GeneExpressionView from "./GeneExpressionView.js";
import GeneGenomeView from "./GeneGenomeView.js";
import GeneProteinsView from "./GeneProteinsView.js";
import GeneSummaryView from "./GeneSummaryView.js";
import GeneTranscriptsView from "./GeneTranscriptsView.js";
import GeneVariantsView from "./GeneVariantsView.js";
import { isGenomeViewerEnabledEntity } from "@/app-utils/entity.ts";
import { type Gene } from "@/graphql.ts";
import { type IGeneState, shouldLoadGene } from "@/redux/modules/gene.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { ENTITY_TYPE } from "@/settings/settings.ts";

/**
 * The GenePage smart component is called from the route corresponding to gene/:geneId.
 * The children prop corresponds to the smart component injected from the nested routes (e.g. gene/:geneId/summary)
 * The GenePage will take care of filling up the basic gene data and the nested child pages may request additional
 * data that is needed to show the nested page (such as transcript and protein child collections for the summary page)
 */
const GeneDetailPage: VFC = () => {
  const { geneId } = useParams<"geneId">();
  const dispatch = useDispatch<ReduxDispatch>();
  const { isLoading, gene } = useSelector<IAppState, IGeneState>((state) => state.gene);

  useEffect(() => {
    void dispatch(shouldLoadGene(Number.parseInt(geneId, 10)));
  }, [dispatch, geneId]);

  const getDecoration = (what: keyof Gene): { disabled: boolean; tooltip: string } => {
    const disabled = isLoading || (gene?.[what] != undefined ? gene?.[what].length === 0 : true);
    const tooltip = `${what.replace(/^\w/, (c) => c.toUpperCase())} have not been imported or calculated for this gene`;
    return { disabled, tooltip };
  };

  const getNavItems = (): IRouteNavItemProps[] => {
    const transcriptDec = getDecoration("transcripts");
    const proteinDec = getDecoration("proteins");
    const variantsDec = {
      disabled: isLoading || (gene && !gene.hasVariants),
      tooltip: "Variants have not been imported or calculated for this gene",
    };
    const genomeviewDisabled = !isGenomeViewerEnabledEntity(gene?.genome);
    const genomeviewDec = {
      disabled: genomeviewDisabled,
      tooltip: "Only genes on genomic sequences can be loaded to the Genome Viewer.",
    };
    return [
      {
        label: "Summary",
        route: "summary",
        pageComponent: <GeneSummaryView />,
        default: true,
        isLoading,
      },
      {
        label: "Transcripts",
        disabled: transcriptDec.disabled,
        disabledTooltip: transcriptDec.tooltip,
        route: "transcripts",
        pageComponent: <GeneTranscriptsView />,
        isLoading,
      },
      {
        label: "Proteins",
        disabled: proteinDec.disabled,
        disabledTooltip: proteinDec.tooltip,
        route: "proteins",
        pageComponent: <GeneProteinsView />,
        isLoading,
      },
      {
        label: "Derived Variants",
        disabled: variantsDec.disabled,
        disabledTooltip: variantsDec.tooltip,
        route: "variants",
        pageComponent: <GeneVariantsView />,
        isLoading,
      },
      {
        label: "Genome View",
        disabled: genomeviewDec.disabled,
        disabledTooltip: genomeviewDec.tooltip,
        route: "genome",
        pageComponent: <GeneGenomeView />,
        isLoading,
      },
      {
        label: "Expression Data",
        route: "expression",
        pageComponent: <GeneExpressionView />,
        isLoading,
      },
    ];
  };

  return (
    <GenericDetailPageLayout
      groupType={ENTITY_TYPE.GENE}
      entityId={Number.parseInt(geneId, 10)}
      getDisplayName={() => gene?.name}
      sidebarNavItems={getNavItems()}
      isLoading={isLoading}
    />
  );
};

export { GeneDetailPage };
